import { Inject } from '@odinjs/odin';
import { FetchAPI, FieldAPI, FormAPI } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterFetch, OnBeforeFetch, OnMount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableFetchEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpResponse } from '@philips-internal/oswald-ui-core/lib/services';
import { FragmentNames, PseudonymAppointmentsStep_3Aliases } from '../../codegen';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { ToastResponse } from '../../interfaces/ToastResponse';
import { HttpServiceError, PatientHttpService } from '../../services/patient-http-service';
import { cast } from '../../utils/cast-helpers';
import { showToastNotificationMessage } from '../../utils/toast-notification-utils';
import PseudonymAppointmentsBaseController from './pseudonym-appointments-base-controller';
import PseudonymAppointmentModel from './pseudonym-appointments-model';
import { AdditionalInformation } from './pseudonym-interfaces';
import { AppointmentsSteps } from './pseudonym-types';

interface FieldTypes {
  dsValorNew: Value;
  dsValorOld: Value;
  nmAtributo: string;
}

@Controller({ component: PseudonymAppointmentsStep_3Aliases.LAYOUT_ADDITIONAL_INFORMATION, fragment: FragmentNames.PSEUDONYM_APPOINTMENTS_STEP_3 })
export default class PseudonymAppointmentsStep3Controller extends PseudonymAppointmentsBaseController {

  @Inject(PseudonymAppointmentsStep_3Aliases.FORM_ADDITIONAL_INFORMATION)
    formAdditionalInformationApi!: FormAPI;

  @Inject(PseudonymAppointmentsStep_3Aliases.FETCH_ADDITIONAL_INFORMATION)
    fetchAdditionalInformationApi!: FetchAPI;

  @Inject(PseudonymAppointmentsStep_3Aliases.FIELD_CHECKBOX_SAVE)
    fieldCheckboxSaveApi!: FieldAPI;

  @OnMount({ target: PseudonymAppointmentsStep_3Aliases.LAYOUT_ADDITIONAL_INFORMATION })
  handleMountAditionalInformation(): void {
    PseudonymAppointmentModel.stepThree.loadAdditionalInformation = () => this.loadAdditionalInformation();
    PseudonymAppointmentModel.stepThree.saveAdditionalInformation = () => this.saveAdditionalInformation();
  }

  loadAdditionalInformation(): void {
    this.fetchAdditionalInformationApi.forceFetch();
  }

  @OnBeforeFetch({ target: PseudonymAppointmentsStep_3Aliases.FETCH_ADDITIONAL_INFORMATION })
  onBeforeFetchAdditionalInformation(event: AbortableFetchEvent): void {
    const currentPatientSelected = PseudonymAppointmentModel.stepOne.currentPatientSelected;
    if (currentPatientSelected?.id) {
      this.fieldCheckboxSaveApi.setVisible(true);
      this.formAdditionalInformationApi.setLoading(true);
      this.fetchAdditionalInformationApi.setParameters({ patientId: currentPatientSelected.id });
    } else {
      this.formAdditionalInformationApi.setLoading(false);
      event.abort();
    }
  }

  @OnAfterFetch({ target: PseudonymAppointmentsStep_3Aliases.FETCH_ADDITIONAL_INFORMATION })
  onAfterFetchAdditionalInformation(): void {
    this.formAdditionalInformationApi.setLoading(false);
    const hasAdditionalInformation = Boolean(Object.keys(this.formAdditionalInformationApi.getData()).length);
    this.setWizardStepDone(AppointmentsSteps.STEP_3_ADDITIONAL_INFORMATION, hasAdditionalInformation);
    this.updateSchedulingModel();
  }

  @OnValidate({ target: PseudonymAppointmentsStep_3Aliases.FORM_ADDITIONAL_INFORMATION })
  onValidateFormAdditionalInformation(event: ValidateEvent): void {
    this.setWizardStepDone(AppointmentsSteps.STEP_3_ADDITIONAL_INFORMATION, event.valid);
    this.updateSchedulingModel();
  }

  private updateSchedulingModel(): void {
    const newValue = this.formAdditionalInformationApi.getRecord() as unknown as AdditionalInformation;
    const oldValue = this.formAdditionalInformationApi.getData() as unknown as AdditionalInformation

    PseudonymAppointmentModel.stepThree.additionalInformation = newValue;
    PseudonymAppointmentModel.stepThree.additionalInformationValues = {
      newValue: newValue,
      oldValue: oldValue
    };
  }

  saveAdditionalInformation(): void {
    const newValues = PseudonymAppointmentModel.stepThree.additionalInformationValues.newValue;
    const oldValues = PseudonymAppointmentModel.stepThree.additionalInformationValues.oldValue;
    const propertySaveAdditionalInfo = 'saveAdditionalInfo';

    const body = {
      campos: Object.keys(newValues)
        .map((propertyName) => {
          const currentPropertyName = propertyName as keyof AdditionalInformation;
          if (propertyName !== propertySaveAdditionalInfo && oldValues && oldValues[currentPropertyName] !== newValues[currentPropertyName]) {
            const oldV = oldValues[currentPropertyName];
            const newV = newValues[currentPropertyName];
            return {
              dsValorNew: newV ? newV : '',
              dsValorOld: oldV ? oldV : '',
              nmAtributo: currentPropertyName,
            };
          }

          return null;
        })
        .filter((campo) => campo !== null) as Array<FieldTypes>
    };

    if (body?.campos?.length === 0) {
      return;
    }

    const currentPatientSelected = PseudonymAppointmentModel.stepOne.currentPatientSelected;
    if (newValues.saveAdditionalInfo && currentPatientSelected) {
      this.sendUpdateRequestAdditionalInformation(body, currentPatientSelected.id);
    }
  }

  private sendUpdateRequestAdditionalInformation(body: Record<string, Value>, patientId: number): Promise<void> {
    return PatientHttpService.post(APPLICATION_ENDPOINTS.POST_PROFILE_INFORMATION, { patientId }, { body })
      .then((response) => { this.handleResponseAdditionalInformation(response) })
      .catch((error: HttpServiceError) => { this.handleResponseAdditionalInformation(cast(error.response)) });
  }

  private handleResponseAdditionalInformation(resp: HttpResponse<unknown>): void {
    showToastNotificationMessage(resp.body as ToastResponse, undefined, 'patient.profile.health.information');
  }

}
